import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { supabase } from '../lib/supabase';
import { logAccessServer } from '../lib/accessLogs';

type Role = 'super_admin' | 'admin' | 'comptable' | 'medecin' | 'patient' | 'secretaire' | 'laborantin' | 'sage_femme' | 'infirmier' | null;

interface PasswordRequirement {
  mustChangePassword: boolean;
  deadlinePassed: boolean;
  deadline: string | null;
  createdByStaff: boolean;
}

interface AuthContextValue {
  user: any | null;
  role: Role;
  userRoles: Role[];
  loading: boolean;
  rolesLoading: boolean;
  passwordRequirement: PasswordRequirement | null;
  signIn: (identifier: string, password: string, method: 'email' | 'phone') => Promise<{ role: string }>;
  signUp: (identifier: string, password: string, userType: 'patient' | 'medecin', loginMethod: 'email' | 'phone', metadata: any, invitationToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  checkPasswordRequirement: () => Promise<void>;
  signInWithGoogle: (redirectTo: string) => Promise<void>;
  refreshRoles: () => Promise<{ primaryRole: Role; allRoles: Role[] }>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Clé de cache pour les rôles — utilisée comme fallback quand fetchUserRoles
// échoue à cause d'une absence de réseau (et non d'un token invalide).
const ROLE_CACHE_KEY = 'csf-role-cache';
const ROLE_CACHE_ALL_KEY = 'csf-role-cache-all';

function cacheRoles(primaryRole: Role, allRoles: Role[]) {
  try {
    localStorage.setItem(ROLE_CACHE_KEY, primaryRole || '');
    localStorage.setItem(ROLE_CACHE_ALL_KEY, JSON.stringify(allRoles || []));
  } catch {
    // Silencieux
  }
}

function getCachedRoles(): { primaryRole: Role; allRoles: Role[] } {
  try {
    const primary = localStorage.getItem(ROLE_CACHE_KEY) || null;
    const all = JSON.parse(localStorage.getItem(ROLE_CACHE_ALL_KEY) || '[]');
    return { primaryRole: (primary as Role) || null, allRoles: all as Role[] };
  } catch {
    return { primaryRole: null, allRoles: [] };
  }
}

function clearCachedRoles() {
  try {
    localStorage.removeItem(ROLE_CACHE_KEY);
    localStorage.removeItem(ROLE_CACHE_ALL_KEY);
  } catch {
    // Silencieux
  }
}

// ========== PURGE UNIFIÉE DES DONNÉES OFFLINE ==========
// Appelée à chaque déconnexion (manuelle, idle timeout, refresh token invalide)
async function clearAllOfflineData() {
  // 1. Purger IndexedDB (données patients, RDV, consultations, etc.)
  try {
    const { clearAllStores } = await import('@/lib/offlineDb');
    await clearAllStores();
  } catch {
    // Silencieux si IndexedDB indisponible
  }

  // 2. Purger la file d'attente de synchronisation + conflits
  try {
    const { clearSyncQueue, clearConflicts } = await import('@/lib/syncQueue');
    clearSyncQueue();
    clearConflicts();
  } catch {
    // Silencieux
  }

  // 3. Purger localStorage (queue persistée, lock, rôles en cache)
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('csf-') || key.startsWith('supabase.'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // Silencieux
  }

  // 4. Purger le Cache API (Service Worker)
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter((name) => name.startsWith('csf-'))
          .map((name) => caches.delete(name)),
      );
    } catch {
      // Silencieux
    }

    // 5. Demander au Service Worker de purger ses propres caches
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
    }
  }
}

// =====================================================

// Vérifie si on est probablement hors-ligne (sans attendre)
function isLikelyOffline(): boolean {
  return !navigator.onLine;
}

// Détecte une erreur réseau transitoire (coupure courte, requête interrompue)
// par opposition à une vraie erreur d'authentification (token invalide/expiré).
// Dans le premier cas, il ne faut PAS déconnecter l'utilisateur.
function isTransientNetworkError(err: any): boolean {
  if (!err) return false;
  const msg = String(err?.message || '');
  return (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('Failed to fetch') ||
    err?.name === 'AuthRetryableFetchError'
  );
}

let rolesFetchInFlight: Promise<{ primaryRole: Role; allRoles: Role[] }> | null = null;

// Wrapper avec déduplication des appels concurrents : signIn ET onAuthStateChange
// appellent fetchUserRoles quasi simultanément pendant la connexion. Sans cette
// garde, deux appels get-user-roles partent en parallèle, l'un des deux peut
// recevoir un 401 si le token est en cours de rafraîchissement, et le rôle met
// plus de temps à se résoudre (d'où la page de login qui reste affichée ~3s).
async function fetchUserRoles(): Promise<{ primaryRole: Role; allRoles: Role[] }> {
  if (rolesFetchInFlight) {
    return rolesFetchInFlight;
  }
  rolesFetchInFlight = fetchUserRolesInternal();
  try {
    return await rolesFetchInFlight;
  } finally {
    rolesFetchInFlight = null;
  }
}

async function fetchUserRolesInternal(): Promise<{ primaryRole: Role; allRoles: Role[] }> {
  try {
    // Appel unique à l'edge function qui bypass RLS côté serveur
    const { data, error } = await supabase.functions.invoke('get-user-roles', {});

    if (error) {
      console.error('[fetchUserRoles] Edge function error:', error);

      if (isLikelyOffline()) {
        console.log('[fetchUserRoles] Hors-ligne, utilisation du cache de rôles');
        return getCachedRoles();
      }

      // 🔧 NE PAS appeler refreshSession() ici. Le refresh token Supabase est à
      // usage unique (rotation). fetchUserRoles est appelé DEUX FOIS en parallèle
      // pendant la connexion (une fois par signIn, une fois par onAuthStateChange).
      // Si get-user-roles échoue, les deux appels entraient dans ce chemin et
      // consommaient le refresh token deux fois → la seconde tentative échouait →
      // SIGNED_OUT → l'utilisateur était renvoyé de force sur la page de login
      // juste après s'être connecté (le fameux rebond /admin → /login).
      // On se contente donc du cache de rôles en fallback, sans toucher au token.
      const cached = getCachedRoles();
      if (cached.primaryRole) {
        console.log('[fetchUserRoles] Erreur edge function → fallback cache de rôles');
        return cached;
      }

      return { primaryRole: null, allRoles: [] };
    }

    if (!data || data.error) {
      console.error('[fetchUserRoles] Edge function returned error:', data?.error);
      // Même logique : hors-ligne → fallback cache
      if (isLikelyOffline()) {
        return getCachedRoles();
      }
      return { primaryRole: null, allRoles: [] };
    }

    const primaryRole: Role = data.primaryRole || null;
    const allRoles: Role[] = data.allRoles || [];

    // Mettre en cache pour le prochain démarrage hors-ligne
    cacheRoles(primaryRole, allRoles);

    return { primaryRole, allRoles };
  } catch (err: any) {
    console.error('[fetchUserRoles] Exception:', err?.message || err);
    // Exception réseau → fallback cache, ne pas déconnecter
    if (isLikelyOffline() || err?.message?.includes('network') || err?.message?.includes('fetch')) {
      console.log('[fetchUserRoles] Erreur réseau probable, fallback cache de rôles');
      return getCachedRoles();
    }
    return { primaryRole: null, allRoles: [] };
  }
}

async function fetchUserRole(): Promise<Role> {
  const { primaryRole } = await fetchUserRoles();
  return primaryRole;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [role, setRole] = useState<Role>(null);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [passwordRequirement, setPasswordRequirement] = useState<PasswordRequirement | null>(null);
  const mountedRef = useRef(true);

  // Idle timeout refs
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTimeoutMinutesRef = useRef(30);

  // Vérifier si le patient doit changer son mot de passe
  const checkPasswordRequirement = async () => {
    if (!user) {
      setPasswordRequirement(null);
      return;
    }

    try {
      const { data: patient, error } = await supabase
        .from('patients')
        .select('created_by_staff, must_change_password, password_change_deadline')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error || !patient) {
        setPasswordRequirement(null);
        return;
      }

      if (patient.created_by_staff && patient.must_change_password && patient.password_change_deadline) {
        const deadline = new Date(patient.password_change_deadline);
        const now = new Date();
        const deadlinePassed = now > deadline;

        setPasswordRequirement({
          mustChangePassword: true,
          deadlinePassed,
          deadline: patient.password_change_deadline,
          createdByStaff: true,
        });
      } else {
        setPasswordRequirement(null);
      }
    } catch (error) {
      // Silencieux : le patient n'a pas de contrainte de mot de passe
    }
  };

  // Effet pour vérifier le mot de passe quand user et role changent
  useEffect(() => {
    if (user && role === 'patient') {
      checkPasswordRequirement();
    } else {
      if (passwordRequirement !== null) {
        setPasswordRequirement(null);
      }
    }
  }, [user, role]);

  // Idle timeout - déconnexion automatique après inactivité
  // SUSPENDU quand l'utilisateur est hors-ligne : pas de déconnexion forcée
  // sans réseau, car l'utilisateur ne pourrait pas se reconnecter.
  useEffect(() => {
    if (!user) {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      return;
    }

    // Charger le timeout depuis la config système
    supabase
      .from('system_config')
      .select('config_value')
      .eq('config_key', 'security')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.config_value?.sessionTimeout) {
          const mins = parseInt(String(data.config_value.sessionTimeout), 10);
          if (mins > 0 && mins <= 1440) {
            sessionTimeoutMinutesRef.current = mins;
          }
        }
      })
      .catch(() => { /* défaut 30 min */ });

    const resetTimer = () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      idleTimeoutRef.current = setTimeout(async () => {
        // Vérifier si on est hors-ligne AVANT de tenter le refresh
        if (isLikelyOffline()) {
          // Hors-ligne : réinitialiser le timer plutôt que de déconnecter
          // L'utilisateur ne peut pas se reconnecter sans réseau.
          if (mountedRef.current) resetTimer();
          return;
        }

        try {
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.getSession();
          if (!refreshError && refreshedSession?.user) {
            if (mountedRef.current) resetTimer();
            return;
          }
          // getSession a échoué : si c'est une erreur réseau transitoire, ne pas déconnecter
          if (refreshError && isTransientNetworkError(refreshError)) {
            if (mountedRef.current) resetTimer();
            return;
          }
          const { data: refreshData, error: retryError } = await supabase.auth.refreshSession();
          if (refreshData?.session?.user) {
            if (mountedRef.current) resetTimer();
            return;
          }
          if (retryError && isTransientNetworkError(retryError)) {
            if (mountedRef.current) resetTimer();
            return;
          }
        } catch (err) {
          // Échec réseau probable — si hors-ligne ou erreur transitoire, reset timer ; sinon déconnecter
          if (mountedRef.current && (isLikelyOffline() || isTransientNetworkError(err))) {
            resetTimer();
            return;
          }
        }
        // Échec refresh confirmé (token vraiment invalide, pas juste offline) → déconnecter
        signOut();
      }, sessionTimeoutMinutesRef.current * 60 * 1000);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));

    resetTimer();

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;

    // Flag pour suivre si la session initiale a été résolue
    let initialSessionResolved = false;
    let safetyTimer: ReturnType<typeof setTimeout>;

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (!mountedRef.current) return;

        if (error) {
          // 🔧 Ne pas détruire la session locale si l'erreur est d'origine réseau
          // (transitoire). Le refresh token est encore valide côté serveur, on le
          // conserve pour que le prochain chargement puisse récupérer la session.
          const isTransientNetwork =
            isLikelyOffline() || isTransientNetworkError(error);

          if (isTransientNetwork) {
            console.warn('[Auth] Erreur réseau getSession, session préservée pour retry');
            initialSessionResolved = true;
            if (mountedRef.current) {
              setLoading(false);
              setRolesLoading(false);
            }
            return;
          }

          // 🔧 Token invalide ou expiré → nettoyer silencieusement le stockage local
          // Sans appel serveur (scope: 'local') car le token est déjà invalide côté serveur
          console.warn('[Auth] Session invalide détectée, nettoyage local...');
          try {
            await supabase.auth.signOut({ scope: 'local' });
          } catch (cleanupErr) {
            // Échec silencieux du nettoyage
          }
          clearCachedRoles();
          initialSessionResolved = true;
          if (mountedRef.current) {
            setLoading(false);
            setRolesLoading(false);
          }
          return;
        }

        if (session?.user) {
          setUser(session.user);
          setRolesLoading(true);

          fetchUserRoles()
            .then(({ primaryRole, allRoles }) => {
              if (!mountedRef.current) return;

              // 🔒 Protection race condition : si fetchUserRoles retourne null
              // mais que la session est active, réutiliser le cache de rôles
              // au lieu de laisser l'utilisateur bloqué sur la page de login.
              if (!primaryRole && session?.user) {
                const cached = getCachedRoles();
                if (cached.primaryRole) {
                  console.warn('[Auth] initializeAuth: rôle non résolu → fallback cache');
                  setRole(cached.primaryRole);
                  setUserRoles(cached.allRoles);
                  setRolesLoading(false);
                  return;
                }
              }

              setRole(primaryRole);
              setUserRoles(allRoles);
              setRolesLoading(false);
            })
            .catch((roleErr: any) => {
              // 🔧 Si fetchUserRoles échoue (ex: refresh token invalide), déconnecter proprement
              console.warn('[Auth] Échec récupération rôles, déconnexion...');
              if (mountedRef.current) {
                supabase.auth.signOut({ scope: 'local' }).catch(() => {});
                clearCachedRoles();
                setUser(null);
                setRole(null);
                setUserRoles([]);
                setRolesLoading(false);
              }
            });

          initialSessionResolved = true;
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      } catch (error) {
        // La session est complètement invalide → attendre onAuthStateChange
        console.warn('[Auth] Exception getSession, attente onAuthStateChange:', (error as Error)?.message);
      }
    };

    initializeAuth();

    // Safety timeout : si après 5 secondes onAuthStateChange n'a toujours pas résolu,
    // on finalise (l'utilisateur est vraiment déconnecté)
    safetyTimer = setTimeout(() => {
      if (!initialSessionResolved && mountedRef.current) {
        initialSessionResolved = true;
        setLoading(false);
        setRolesLoading(false);
      }
    }, 5000);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mountedRef.current) return;

      initialSessionResolved = true;
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }

      // 🔧 Si Supabase signale une déconnexion (ex: refresh token invalide),
      // nettoyer le stockage local ET l'état React
      // 🔧 Optimisation critique : sur TOKEN_REFRESHED, l'utilisateur et son rôle
      // ne changent PAS. Re-fetcher les rôles ici déclenche un appel inutile vers
      // get-user-roles à CHAQUE rafraîchissement de token. Si le token se
      // rafraîchit fréquemment (JWT court ou rotation), cela génère un déluge de
      // requêtes → 401 + 429 (rate limit) → SIGNED_OUT → rebond vers login.
      // On met simplement à jour la référence de l'utilisateur, sans re-fetch.
      if (_event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user);
        }
        setLoading(false);
        return;
      }

      if (_event === 'SIGNED_OUT') {
        // Purge complète des données offline (IndexedDB, sync queue, cache SW, localStorage)
        clearAllOfflineData().finally(() => {
          if (!mountedRef.current) return;
          setUser(null);
          setRole(null);
          setUserRoles([]);
          setRolesLoading(false);
          setPasswordRequirement(null);
          setLoading(false);
        });
        return;
      }

      if (session?.user) {
        setUser(session.user);
        setRolesLoading(true);
        
        fetchUserRoles()
          .then(({ primaryRole, allRoles }) => {
            if (!mountedRef.current) return;

            // 🔒 Protection race condition : si fetchUserRoles retourne null
            // mais que l'utilisateur a toujours une session active, ne pas
            // écraser le rôle existant. Utiliser le cache ou garder l'ancien.
            if (!primaryRole && session?.user) {
              const cached = getCachedRoles();
              if (cached.primaryRole) {
                console.warn('[Auth] onAuthStateChange: fetchUserRoles a retourné null mais session active → utilisation du cache');
                setRole(cached.primaryRole);
                setUserRoles(cached.allRoles);
                setRolesLoading(false);
                return;
              }
              // Si on a déjà un rôle en state et que la session est active,
              // ne pas l'écraser à null (race condition après signIn)
              setRolesLoading(false);
              return;
            }

            setRole(primaryRole);
            setUserRoles(allRoles);
            setRolesLoading(false);
          })
          .catch((roleErr: any) => {
            if (mountedRef.current) {
              // Distinguer erreur réseau (offline) d'une vraie erreur d'auth
              if (isLikelyOffline()) {
                // Hors-ligne : garder la session, utiliser le cache de rôles
                console.warn('[Auth] onAuthStateChange: fetchUserRoles échec réseau, utilisation cache');
                const cached = getCachedRoles();
                if (mountedRef.current) {
                  setRole(cached.primaryRole);
                  setUserRoles(cached.allRoles);
                  setRolesLoading(false);
                }
                return;
              }
              // Vraie erreur (token invalide, compte supprimé) → déconnecter
              console.warn('[Auth] onAuthStateChange: échec fetchUserRoles, déconnexion...');
              if (mountedRef.current) {
                supabase.auth.signOut({ scope: 'local' }).catch(() => {});
                clearCachedRoles();
                setUser(null);
                setRole(null);
                setUserRoles([]);
                setRolesLoading(false);
              }
            }
          });
      } else {
        setUser(null);
        setRole(null);
        setUserRoles([]);
        setRolesLoading(false);
        setPasswordRequirement(null);
      }
      setLoading(false);
    });

    return () => {
      mountedRef.current = false;
      if (safetyTimer) {
        clearTimeout(safetyTimer);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (identifier: string, password: string, method: 'email' | 'phone') => {
    try {
      let authResult;

      if (method === 'email') {
        authResult = await supabase.auth.signInWithPassword({
          email: identifier,
          password: password,
        });
      } else {
        let normalizedPhone = identifier.trim().replace(/\s+/g, '');
        if (normalizedPhone.startsWith('0')) {
          normalizedPhone = '+229' + normalizedPhone.substring(1);
        }
        if (!normalizedPhone.startsWith('+')) {
          normalizedPhone = '+229' + normalizedPhone;
        }
        
        const phoneEmail = `phone_${normalizedPhone.replace('+', '')}@cliniquesaintefamille.local`;
        
        authResult = await supabase.auth.signInWithPassword({
          email: phoneEmail,
          password: password,
        });
      }

      const { data, error } = authResult;

      if (error) {
        console.error('[Auth] Erreur de connexion :', error);
        throw error;
      }

      if (!data.user) {
        throw new Error('Aucun utilisateur trouvé');
      }

      // 🔧 Suppression du setSession redondant : signInWithPassword stocke déjà
      // la session de façon synchrone. L'ancien setSession({access_token, refresh_token})
      // rejouait le refresh token (rotation à usage unique côté Supabase), ce qui
      // l'invalidait. Quelques secondes plus tard, l'auto-refresh échouait → SIGNED_OUT
      // → l'utilisateur était renvoyé de force sur la page de login.

      // Double vérification : on s'assure que la session est bien chargée
      let retries = 0;
      const maxRetries = 10;
      while (retries < maxRetries) {
        const { data: checkSession } = await supabase.auth.getSession();
        if (checkSession.session) break;
        retries++;
        await new Promise((r) => setTimeout(r, 200));
      }

      setUser(data.user);
      setRolesLoading(true);

      const { primaryRole: userRole, allRoles } = await fetchUserRoles();

      if (!userRole) {
        console.error('[Auth] Aucun rôle trouvé pour userId:', data.user.id);
        throw new Error('Aucun profil trouvé pour cet utilisateur');
      }

      setRole(userRole);
      setUserRoles(allRoles);
      setRolesLoading(false);
      return { role: userRole };
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (
    identifier: string,
    password: string,
    userType: 'patient' | 'medecin',
    loginMethod: 'email' | 'phone',
    metadata: any,
    invitationToken?: string
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('register-user', {
        body: {
          identifier,
          password,
          userType,
          loginMethod,
          metadata,
          invitationToken,
        },
      });

      const serverMessage = data?.error || (error as any)?.context?.error;
      if (serverMessage) {
        throw new Error(serverMessage);
      }

      if (error) {
        console.error('[Auth] Erreur d\'inscription :', error);
        throw error;
      }

      await signIn(identifier, password, loginMethod);
    } catch (error: any) {
      console.error('[Auth] Échec de l\'inscription :', error);
      throw new Error(error.message || 'Erreur lors de la création du compte');
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          throw new Error('Lien de réinitialisation invalide ou expiré');
        }
      } else {
        throw new Error('Aucune session active. Veuillez refaire une demande de réinitialisation.');
      }
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) throw error;
  };

  const signOut = async () => {
    try {
      setLoading(true);

      // Logger la déconnexion avant de déconnecter
      if (user) {
        logAccessServer({
          userId: user.id,
          userRole: role || 'unknown',
          actionType: 'LOGOUT',
          resourceType: 'SESSION',
          details: {
            operation: 'Déconnexion',
            userRoles: userRoles,
          },
        }).catch(() => { /* silencieux si échec */ });

        // Terminer la session dans la BDD
        const sessionId = localStorage.getItem('current_session_id');
        if (sessionId) {
          try {
            await supabase.functions.invoke('end-user-session', {
              body: { sessionId, actionsPerformed: { logoutType: 'manual' } },
            });
          } catch (err) {
            // Silencieux
          }
          localStorage.removeItem('current_session_id');
        }
      }

      // Nettoyer le timer d'inactivité
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }

      // ========== PURGE OFFLINE DATA (sécurité données de santé) ==========
      await clearAllOfflineData();
      // ================================================================

      await supabase.auth.signOut();
      if (!mountedRef.current) return;
      setUser(null);
      setRole(null);
      setUserRoles([]);
      setRolesLoading(false);
      setPasswordRequirement(null);
      
      // Redirection vers la page d'accueil
      setTimeout(() => {
        if (window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/');
        } else {
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      if (mountedRef.current) setLoading(false);
    }
  };

  const signInWithGoogle = async (redirectTo: string) => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) throw error;
  };

  // Re-fetch les rôles utilisateur et mettre à jour le state AuthContext.
  // Utilisé après un link-google-account réussi pour que le contexte reflète
  // immédiatement le nouveau rôle sans rechargement de page.
  const refreshRoles = async (): Promise<{ primaryRole: Role; allRoles: Role[] }> => {
    setRolesLoading(true);
    try {
      const { primaryRole: newRole, allRoles: newAllRoles } = await fetchUserRoles();
      if (mountedRef.current) {
        setRole(newRole);
        setUserRoles(newAllRoles);
        setRolesLoading(false);
        if (newRole === 'patient') {
          checkPasswordRequirement();
        }
      }
      return { primaryRole: newRole, allRoles: newAllRoles };
    } catch {
      if (mountedRef.current) {
        setRolesLoading(false);
      }
      return { primaryRole: null, allRoles: [] };
    }
  };

  return (
    <AuthContext.Provider value={{ user, role, userRoles, loading, passwordRequirement, signIn, signUp, signOut, resetPassword, updatePassword, checkPasswordRequirement, signInWithGoogle, refreshRoles, rolesLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};